import { api, ApiError } from "../lib/api";

// Keep ambiguous requests in this tab so a manual retry uses the same identity.
const requests = new Map<string, { id: string; uncertain: boolean; promise?: Promise<unknown> }>();

export function hasUncertainAnswer(runId: string, input: unknown) { return requests.get(`${runId}:${JSON.stringify(input)}`)?.uncertain === true; }

export async function waitForPendingAnswers(runId: string) {
  const pending = [...requests.entries()].filter(([key]) => key.startsWith(`${runId}:`));
  await Promise.all(pending.map(([key, request]) => request.promise ?? deliverAnswer(runId, JSON.parse(key.slice(runId.length + 1)))));
  if ([...requests.keys()].some((key) => key.startsWith(`${runId}:`))) {
    throw new Error("Noch nicht alle Antworten wurden bestätigt. Bitte den Abschluss erneut speichern.");
  }
}

export function deliverAnswer<Response>(runId: string, input: unknown): Promise<Response> {
  const body = JSON.stringify(input);
  const key = `${runId}:${body}`;
  let request = requests.get(key);
  if (request?.promise) return request.promise as Promise<Response>;
  if (!request) {
    if (requests.size >= 64) return Promise.reject(new Error("Zu viele unbestätigte Antworten. Bitte zunächst die Verbindung wiederherstellen."));
    request = { id: crypto.randomUUID(), uncertain: false };
    requests.set(key, request);
  }
  const requestId = request.id;
  request.promise = (async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const result = await api<Response>(`/learning/runs/${encodeURIComponent(runId)}/answers`, {
          method: "POST", body,
          headers: { "X-Answer-Request-Id": requestId },
          signal: AbortSignal.timeout(12_000),
        });
        requests.delete(key);
        return result;
      } catch (error) {
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) {
          // Preserve the identity: an earlier attempt may already have committed.
          if (!requests.get(key)?.uncertain) requests.delete(key);
          throw error;
        }
        const pending = requests.get(key);
        if (pending) pending.uncertain = true;
        if (attempt === 2) throw new Error("Die Antwort wurde noch nicht bestätigt. Bitte erneut überprüfen; dieselbe Abgabe wird nicht doppelt gespeichert.");
        await new Promise((resolve) => setTimeout(resolve, error instanceof ApiError && error.status === 429 ? 12_000 : 1_000 * (attempt + 1)));
      }
    }
    throw new Error("Die Antwort wurde nicht bestätigt.");
  })().finally(() => { const pending = requests.get(key); if (pending) pending.promise = undefined; });
  return request.promise as Promise<Response>;
}
